import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { EditableTableColumn } from '../../models/editable-table-column.model';
import { AppToastService } from '../../services/app-toast.service';
import { getDisplayText, isColumnEmpty, isRequiredEmpty } from './editable-table-cell.util';

@Component({
  selector: 'app-editable-table',
  templateUrl: './editable-table.component.html',
  styleUrls: ['./editable-table.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditableTableComponent<T = any> implements OnChanges, AfterViewInit, OnDestroy {
  @Input() columns: EditableTableColumn<T>[] = [];
  @Input() rows: T[] = [];
  @Input() searchTerm: string = '';
  @Input() loading = false;
  @Input() emptyMessage = 'No rows.';
  /** Shows the footer strip below the grid — content is up to the caller, projected via <ng-content>. */
  @Input() showFooter = false;
  /** Shows a discrete, zero-padded row number ("01", "02"...) to the left of the row
   *  checkbox, reflecting each row's position in the current (visible) order. */
  @Input() showRowNumber = false;
  /** Shows a bordered "+/-" column for toggling extra columns on/off, rendered
   *  right after the column at `toggleColumnAfterIndex` (or at the very end if
   *  null) — not spliced into the extra columns themselves, so it stays put as
   *  a fixed divider between "always visible" and "shown on demand". */
  @Input() showToggleColumn = false;
  /** Index (within `columns`) after which the toggle column renders. Null renders
   *  it at the very end. */
  @Input() toggleColumnAfterIndex: number | null = null;
  /** Whether the extra columns are currently shown — drives the icon ("+" vs "−"). */
  @Input() toggleExpanded = false;
  @Output() toggleExpandedChange = new EventEmitter<boolean>();
  /** Field names currently showing a skeleton instead of their real editor —
   *  used for the toggle column's brief "columns are appearing" transition,
   *  which has no real fetch behind it (unlike `loading`, which replaces the
   *  whole grid — this only affects specific cells). */
  @Input() loadingFields: string[] = [];

  @Output() selectedRowsChange = new EventEmitter<T[]>();
  @Output() deleteRequested = new EventEmitter<T[]>();

  /** Stable reference used as the dropdown `options` fallback (see template) —
   *  `col.options || []` would allocate a new array every change-detection
   *  pass while a column's options haven't loaded yet, and since Angular
   *  input bindings compare by reference, that makes `ngOnChanges` fire on
   *  every single CD tick in the dropdown cell components. With dozens of
   *  rows this becomes a self-sustaining loop (each fresh array triggers
   *  more internal reactivity, which triggers more CD) that pins the main
   *  thread. A single shared empty array keeps the reference stable. */
  readonly emptyOptions: any[] = [];

  visibleRows: T[] = [];
  selectedRows: T[] = [];
  selectedColumns: EditableTableColumn<T>[] = [];
  sortField: string | null = null;
  sortOrder: 1 | -1 = 1;

  /** Values picked in the bulk-edit bar, keyed by column field. */
  bulkValues: Record<string, any> = {};

  @ViewChild('tableShell') tableShell: ElementRef<HTMLElement> | undefined;
  @ViewChild('topScrollbar') topScrollbar: ElementRef<HTMLElement> | undefined;
  @ViewChild('topScrollbarInner') topScrollbarInner: ElementRef<HTMLElement> | undefined;
  private tableScrollElement: HTMLElement | null = null;
  private readonly onTableScroll = () => this.syncTopScrollbarFromTable();
  private _syncingScroll = false;

  constructor(
    private readonly toastService: AppToastService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngAfterViewInit(): void {
    this.scheduleScrollbarSync();
  }

  ngOnDestroy(): void {
    this.tableScrollElement?.removeEventListener('scroll', this.onTableScroll);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.scheduleScrollbarSync();
  }

  /** Mirrors the top scrollbar's drag into the real (bottom) table scroll. */
  onTopScroll(): void {
    if (this._syncingScroll || !this.tableScrollElement || !this.topScrollbar) return;
    this._syncingScroll = true;
    this.tableScrollElement.scrollLeft = this.topScrollbar.nativeElement.scrollLeft;
    this._syncingScroll = false;
  }

  private syncTopScrollbarFromTable(): void {
    if (this._syncingScroll || !this.topScrollbar?.nativeElement || !this.tableScrollElement) return;
    this._syncingScroll = true;
    this.topScrollbar.nativeElement.scrollLeft = this.tableScrollElement.scrollLeft;
    this._syncingScroll = false;
  }

  onToggleColumnClick(): void {
    this.toggleExpandedChange.emit(!this.toggleExpanded);
  }

  private syncTopScrollbarWidth(): void {
    if (!this.tableScrollElement || !this.topScrollbarInner?.nativeElement || !this.topScrollbar?.nativeElement) {
      return;
    }
    const scrollWidth = this.tableScrollElement.scrollWidth;
    const clientWidth = this.tableScrollElement.clientWidth;
    this.topScrollbarInner.nativeElement.style.width = scrollWidth + 'px';
    this.topScrollbar.nativeElement.style.display = scrollWidth > clientWidth + 2 ? 'block' : 'none';
  }

  private bindTableScrollElement(): void {
    const host = this.tableShell?.nativeElement;
    if (!host) return;
    const scrollCandidates = Array.from(
      host.querySelectorAll<HTMLElement>(
        '.p-datatable-table-container, .p-datatable-wrapper, .p-datatable-scrollable-body',
      ),
    );
    const scrollElement =
      scrollCandidates.find((element) => element.scrollWidth > element.clientWidth + 2) ?? scrollCandidates[0];
    if (!scrollElement || scrollElement === this.tableScrollElement) return;
    this.tableScrollElement?.removeEventListener('scroll', this.onTableScroll);
    this.tableScrollElement = scrollElement;
    this.tableScrollElement.addEventListener('scroll', this.onTableScroll, { passive: true });
  }

  /** Re-binds to the table's scroll element and re-measures the mirror's width —
   *  run after the view settles (rows/columns changed, window resized, or first
   *  render) since PrimeNG creates its scroll wrapper asynchronously. */
  private scheduleScrollbarSync(): void {
    setTimeout(() => {
      this.bindTableScrollElement();
      this.syncTopScrollbarWidth();
    }, 0);
    setTimeout(() => {
      this.bindTableScrollElement();
      this.syncTopScrollbarWidth();
    }, 100);
  }

  /** Call after mutating row/column data from outside an event this table's own
   *  template owns (e.g. an async save completing in the parent page). With
   *  OnPush, in-place mutations on an unchanged `rows`/`columns` reference
   *  otherwise go unnoticed — this forces a recheck. */
  refresh(): void {
    this.recompute();
    this.cdr.markForCheck();
  }

  /** Cached results for `bulkColumns`/`bulkRows` — see `recomputeBulkGroups`
   *  for why these can't be plain getters. */
  private _bulkColumns: EditableTableColumn<T>[] = [];
  private _bulkRows: EditableTableColumn<T>[][] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rows']) {
      // Drop selection of rows that no longer exist (e.g. after a delete).
      this.selectedRows = this.selectedRows.filter((r) => this.rows.includes(r));
    }
    if (changes['columns']) {
      this.selectedColumns = this.selectedColumns.filter((c) => this.columns.includes(c));
      this.recomputeBulkGroups();
    }
    if (changes['rows'] || changes['columns'] || changes['searchTerm']) {
      this.recompute();
    }
    if (changes['rows'] || changes['columns']) {
      this.scheduleScrollbarSync();
    }
  }

  get bulkColumns(): EditableTableColumn<T>[] {
    return this._bulkColumns;
  }

  /** Bulk-edit bar layout: columns grouped by `bulkRow` (0-based), in row order. */
  get bulkRows(): EditableTableColumn<T>[][] {
    return this._bulkRows;
  }

  // Building these as a template-bound getter (recomputed on every single
  // change-detection cycle, not just when `columns` actually changes) allocates
  // a brand-new array of brand-new arrays every time. `*ngFor` over `bulkRows`
  // then sees a different reference every cycle and — with no trackBy — tears
  // down and rebuilds every bulk-bar widget (including the datepickers) on
  // every CD tick. Each teardown/rebuild schedules its own async work, which
  // triggers the next CD tick, forming a self-sustaining loop that pins the
  // main thread — independent of how many rows the grid has. Caching the
  // grouping and only recomputing it when `columns` genuinely changes keeps
  // the references (and therefore the DOM) stable in between.
  private recomputeBulkGroups(): void {
    this._bulkColumns = this.columns.filter((c) => c.bulkEditable);

    const rows = new Map<number, EditableTableColumn<T>[]>();
    for (const col of this._bulkColumns) {
      const rowIndex = col.bulkRow ?? 0;
      if (!rows.has(rowIndex)) rows.set(rowIndex, []);
      rows.get(rowIndex)!.push(col);
    }
    this._bulkRows = Array.from(rows.keys())
      .sort((a, b) => a - b)
      .map((key) => rows.get(key)!);
  }

  // `bulkRows`' own sub-arrays (one per bulkRow index) are freshly built by
  // recomputeBulkGroups() every time `columns` changes reference — which now
  // happens on every backend dropdown search resolving (see syncColumnOptions
  // in call-review.component.ts), including just from opening a Vessel/Port/
  // Shipowner dropdown (dropdownMode="current" searches on open). Without an
  // explicit trackBy, *ngFor's default identity-based diffing sees a brand-new
  // "rowCols" array reference each time — even though the grouping itself
  // hasn't actually changed — and tears down + recreates every bulk-bar widget
  // in that row, closing whatever the user had just opened. Tracking by the
  // bulkRow index (stable across recomputes as long as the grouping structure
  // itself doesn't change) keeps the widgets' component instances alive.
  trackByBulkRowIndex(index: number): number {
    return index;
  }

  trackByColumnField(_index: number, col: EditableTableColumn<T>): string {
    return col.field;
  }

  // --- display text / search / sort ---------------------------------------------
  // Discrete actions only (search input, sort click, bulk apply/clear) recompute
  // `visibleRows` — never on a per-keystroke cell edit, or typing in a text cell
  // would lose focus every time p-table gets handed a brand-new array reference.

  getDisplayText(row: T, col: EditableTableColumn<T>): string {
    return getDisplayText(row, col);
  }

  onSort(field: string): void {
    if (this.sortField === field) {
      this.sortOrder = this.sortOrder === 1 ? -1 : 1;
    } else {
      this.sortField = field;
      this.sortOrder = 1;
    }
    this.recompute();
  }

  private recompute(): void {
    let result = [...this.rows];

    const term = (this.searchTerm || '').trim().toLowerCase();
    if (term) {
      result = result.filter((row) =>
        this.columns.some((col) => this.getDisplayText(row, col).toLowerCase().includes(term)),
      );
    }

    if (this.sortField) {
      const col = this.columns.find((c) => c.field === this.sortField);
      if (col) {
        const order = this.sortOrder;
        result.sort((a, b) => {
          if (col.type === 'date') {
            const t1 = (a as any)[col.field] ? new Date((a as any)[col.field]).getTime() : NaN;
            const t2 = (b as any)[col.field] ? new Date((b as any)[col.field]).getTime() : NaN;
            if (isNaN(t1) && isNaN(t2)) return 0;
            if (isNaN(t1)) return -order;
            if (isNaN(t2)) return order;
            return order * (t1 - t2);
          }
          const v1 = this.getDisplayText(a, col);
          const v2 = this.getDisplayText(b, col);
          if (!v1 && v2) return -order;
          if (v1 && !v2) return order;
          if (!v1 && !v2) return 0;
          return order * v1.localeCompare(v2, undefined, { numeric: true, sensitivity: 'base' });
        });
      }
    }

    this.visibleRows = result;
  }

  // --- empty / required tracking --------------------------------------------------

  isColumnEmpty(row: T, col: EditableTableColumn<T>): boolean {
    return isColumnEmpty(row, col);
  }

  isRequiredEmpty(row: T, col: EditableTableColumn<T>): boolean {
    return isRequiredEmpty(row, col);
  }

  get hasEmptyRequired(): boolean {
    return this.columns.some(
      (c) => c.required && this.rows.some((r) => this.isColumnEmpty(r, c)),
    );
  }

  // --- selection -------------------------------------------------------------------

  onSelectionChange(selection: T[]): void {
    this.selectedRows = selection;
    this.selectedRowsChange.emit(selection);

    // Row selection and column selection are mutually exclusive — you're either
    // bulk-editing rows or clearing columns, never a mix — except the one
    // deliberate "select everything" case: the header checkbox selecting every
    // row also selects every column, so it reads as "the whole grid" at once.
    if (this.visibleRows.length > 0 && selection.length === this.visibleRows.length) {
      this.selectedColumns = [...this.columns];
    } else {
      this.selectedColumns = [];
    }
  }

  isRowSelected(row: T): boolean {
    return this.selectedRows.includes(row);
  }

  /** True once every row (and, by the mutual-exclusion rule, every column) is
   *  selected via the header "select all" checkbox — the one case where row and
   *  column checkboxes are checked together. Used to mute their color to gray,
   *  keeping the primary-colored checked state reserved for a deliberate,
   *  specific selection. */
  get isAllSelected(): boolean {
    return this.visibleRows.length > 0 && this.selectedRows.length === this.visibleRows.length;
  }

  // Row-selection border shadow moved to EditableTableRowComponent (needs the
  // adjacent rows' selected state, passed down as inputs, to cap a contiguous
  // selected run correctly — see that component for the full logic).

  // Same idea for a selected column: the header cell is always the top cap, and
  // only the very last row is the bottom cap — every row in between only draws
  // its left/right edge, so the vertical line runs unbroken top to bottom. And,
  // two ADJACENT selected columns shouldn't seam against each other either —
  // suppress the shared left/right edge between them so they merge into one
  // continuous block instead of two boxes side by side. Suppressed entirely
  // when "select all" is active, same as rows.
  //
  // This is the HEADER-cell version only (always caps the top edge) — body
  // cells compute their own via EditableTableRowComponent, which also knows
  // whether it's the last row.
  headerColumnBorderShadow(col: EditableTableColumn<T>): string | null {
    if (this.isAllSelected || !this.isColumnSelected(col)) return null;

    const index = this.columns.indexOf(col);
    const prevSelected = index > 0 && this.isColumnSelected(this.columns[index - 1]);
    const nextSelected =
      index < this.columns.length - 1 && this.isColumnSelected(this.columns[index + 1]);

    const parts: string[] = ['inset 0 2px 0 0 var(--primary-500)'];
    if (!prevSelected) parts.push('inset 2px 0 0 0 var(--primary-500)');
    if (!nextSelected) parts.push('inset -2px 0 0 0 var(--primary-500)');
    return parts.join(', ');
  }

  // --- cell editing ------------------------------------------------------------------

  /** Used by date/dropdown/dropdown-search cells — discrete selection, safe to recompute.
   *  `emitValueChanged` is false from the bulk-edit path (see applyBulk()) — a column's
   *  onValueChanged hook (e.g. call-review's linked-field confirmation) is meant for a
   *  single edited cell, not one confirmation dialog per row in a bulk apply. */
  onCellValueChanged(
    row: T,
    col: EditableTableColumn<T>,
    value: any,
    emitValueChanged = true,
  ): void {
    const previousValue = (row as any)[col.field];
    (row as any)[col.field] = value;
    if (col.type === 'dropdown' || col.type === 'dropdown-search') {
      const opt = col.options?.find((o) => o[col.optionValue ?? 'id'] === value);
      col.onOptionSelected?.(row, opt);
    }
    if (emitValueChanged) col.onValueChanged?.(row, value, previousValue);
    this.recompute();
  }

  private clearFields(row: T, cols: EditableTableColumn<T>[]): void {
    for (const col of cols) {
      (row as any)[col.field] = null;
      for (const companion of col.companionFields ?? []) {
        (row as any)[companion] = null;
      }
    }
  }

  // --- bulk-edit bar -----------------------------------------------------------------
  // Two ways to reach the bulk-edit bar, and it must behave sensibly for both:
  //   - Row mode (rows ticked): any bulk-editable field can be set/cleared on
  //     those specific rows.
  //   - Column mode (a column checkbox ticked, no rows ticked): only the
  //     field(s) matching the selected column(s) are editable, and Apply/Clear
  //     act on EVERY row for just those field(s) — you're editing the column,
  //     not a set of rows. Delete stays row-only (there's no such thing as
  //     "deleting a column").

  get hasBulkTargets(): boolean {
    return this.selectedRows.length > 0 || this.selectedColumns.length > 0;
  }

  /** Whether a given bulk-editable column's input should be enabled right now. */
  isBulkColumnEnabled(col: EditableTableColumn<T>): boolean {
    return this.selectedRows.length > 0 || this.selectedColumns.includes(col);
  }

  /** Apply should stay disabled until the user actually picked at least one
   *  value below — having rows/columns selected alone isn't enough to apply. */
  get hasBulkValueSet(): boolean {
    return Object.values(this.bulkValues).some((v) => v !== null && v !== undefined && v !== '');
  }

  applyBulk(): void {
    if (!this.hasBulkTargets) {
      this.toastService.warning('Nothing selected', 'Tick rows, or select a column, first.');
      return;
    }

    const entries = Object.entries(this.bulkValues).filter(([field, value]) => {
      if (value === null || value === undefined || value === '') return false;
      const col = this.columns.find((c) => c.field === field);
      return !!col && this.isBulkColumnEnabled(col);
    });
    if (!entries.length) {
      this.toastService.warning('Nothing to apply', 'Pick at least one field value first.');
      return;
    }

    const targets = this.selectedRows.length ? this.selectedRows : this.rows;
    for (const [field, value] of entries) {
      const col = this.columns.find((c) => c.field === field)!;
      for (const row of targets) {
        this.onCellValueChanged(row, col, value, false);
      }
      // Reset the editor back to empty once applied — it was a one-shot value
      // to push onto the target rows, not a value that should linger as if
      // still pending.
      this.bulkValues[field] = null;
    }

    const n = targets.length;
    this.toastService.success('Applied', `Updated ${n} ${n === 1 ? 'row' : 'rows'}.`);
  }

  clearBulk(): void {
    if (!this.hasBulkTargets) {
      this.toastService.warning('Nothing selected', 'Tick rows, or select a column, first.');
      return;
    }

    if (this.selectedRows.length) {
      for (const row of this.selectedRows) {
        this.clearFields(row, this.columns);
      }
      this.recompute();

      const n = this.selectedRows.length;
      this.toastService.success('Cleared', `Cleared all fields on ${n} selected ${n === 1 ? 'row' : 'rows'}.`);
      return;
    }

    this.confirmClearColumns();
  }

  // --- column selection / clear -------------------------------------------------------
  // Ticking a column checkbox never clears/applies anything by itself — it only
  // selects that column (multiple at once, same as rows); Apply/Clear in the
  // bulk-edit bar are always the deliberate second step.

  toggleColumnSelection(col: EditableTableColumn<T>): void {
    const wasSelected = this.selectedColumns.includes(col);
    this.selectedColumns = wasSelected
      ? this.selectedColumns.filter((c) => c !== col)
      : [...this.selectedColumns, col];

    // Deselecting a column abandons whatever pending bulk value it had —
    // it was never applied, so there's nothing left to hold onto here.
    if (wasSelected) {
      this.bulkValues[col.field] = null;
    }

    // Mutually exclusive with row selection (see onSelectionChange) — ticking
    // a column checkbox always means "column mode", so drop any row selection.
    if (this.selectedRows.length) {
      this.selectedRows = [];
      this.selectedRowsChange.emit(this.selectedRows);
    }
  }

  isColumnSelected(col: EditableTableColumn<T>): boolean {
    return this.selectedColumns.includes(col);
  }

  private confirmClearColumns(): void {
    if (!this.selectedColumns.length) return;

    for (const row of this.rows) {
      this.clearFields(row, this.selectedColumns);
    }
    this.recompute();

    const n = this.selectedColumns.length;
    const label = n === 1 ? `"${this.selectedColumns[0].header}"` : `${n} fields`;
    this.toastService.success('Cleared', `Cleared ${label} on ${this.rows.length} rows.`);
    this.selectedColumns = [];
  }

  // --- delete --------------------------------------------------------------------------
  // Never mutates `rows` — only emits the intent. The consuming page confirms and
  // reassigns `rows` itself, which this component then reacts to via ngOnChanges.

  requestDeleteSelected(): void {
    if (!this.selectedRows.length) {
      this.toastService.warning('No rows selected', 'Tick the rows you want to delete first.');
      return;
    }
    this.deleteRequested.emit([...this.selectedRows]);
  }
}
